import { useCallback, useContext } from "react";
import { ObservationsContext } from "../../ObservationsContextContainer";
import { MapContext } from "../../../../App/MapContextContainer";
import { useTranslation } from "react-i18next";

export function useGetObservationPopupLine() {
    const observationsContext = useContext(ObservationsContext);
    const mapContext = useContext(MapContext);
    const { t, i18n } = useTranslation("observations");

    const getObservationLine = useCallback(
        (feature: any) => {
            const graphic = feature.attributes ? feature : feature.graphic;

            if (observationsContext.observationsState.allTaxons && observationsContext.observationsState.allCollections) {
                const taxon = observationsContext.observationsState.allTaxons.find(t => t.id === graphic.attributes["unit.linkings.taxon.id"]);

                const unknownTaxonName = t("tuntematonLaji");
                const taxonName = taxon ? (i18n.language === "en" ? taxon.nameEn : taxon.nameFi) : unknownTaxonName;

                const taxonNameWithFirstCharCaps = taxonName.charAt(0).toUpperCase() + taxonName.substring(1);
                const collection = observationsContext.observationsState.allCollections.find(c => c.id === graphic.attributes["document.collectionId"]);

                const unknownCollectionName = t("tuntematonKokoelma");
                const collectionName = collection ? (i18n.language === "en" ? collection.nameEn : collection.nameFi) : unknownCollectionName;

                const displayDate = new Date(graphic.attributes["gathering.eventDate.begin"]);
                const displayDateFormatted = displayDate.toLocaleDateString();

                const declarationArrivalDate = graphic.attributes["declarationArrivalDate"] ?
                    new Date(graphic.attributes["declarationArrivalDate"]).toLocaleDateString() :
                    null;

                const declarationId = graphic.attributes["notificationObjectId"];
                const standNumber = graphic.attributes["standNumber"];
                const cuttingRealizationPractices: string = graphic.attributes["cuttingRealizationPractices"];
                let cuttingRealizationPracticesText = "";

                if (cuttingRealizationPractices) {
                    const parts = cuttingRealizationPractices.split(".");

                    cuttingRealizationPracticesText = parts.reduce((prev, curr) => {
                        const crpCleanedUp = parseInt(curr, 10);

                        if (isNaN(crpCleanedUp) === false) {
                            return prev + " " + mapContext.getNotificationTypeText(crpCleanedUp);
                        }

                        return prev;
                    }, "");
                }

                const accuracyMeters = graphic.attributes["gathering.interpretations.coordinateAccuracy"] || undefined;
                const clashRadiusMeters = graphic.attributes["clashRadius"] || undefined;

                if (i18n.language === "en") {
                    return `
                        ${(clashRadiusMeters && clashRadiusMeters > 0) ?
                            `<p>
                                <b>Observation processed with ${clashRadiusMeters}m buffer</b>
                            </p>` : ""}
                        <p>
                            ${taxonNameWithFirstCharCaps}. ${collectionName}. ${displayDateFormatted}. 
                            ${declarationArrivalDate ? ("Declaration arrival date: " + declarationArrivalDate + ".") : ""}
                            ${declarationId ? ("Declaration id: " + declarationId + ".") : ""}
                            ${standNumber ? ("Stand id: " + standNumber + ".") : ""}
                            ${cuttingRealizationPractices ? ("Cutting type: " + cuttingRealizationPracticesText + ".") : ""}
                            ${(accuracyMeters && accuracyMeters !== 1) ? "NOTE! Obs. accuracy " + accuracyMeters + "m." : ""} 
                            <a style="color: lightblue" href="${graphic.attributes["unit.unitId"]}">Laji.fi-link</a>
                        </p>`;
                }
                else {
                    return `
                        ${(clashRadiusMeters && clashRadiusMeters > 0) ?
                            `<p>
                                <b>Havainto käsitelty ${clashRadiusMeters}m bufferilla</b>
                            </p>` : ""}
                        <p>
                            ${taxonNameWithFirstCharCaps}. ${collectionName}. ${displayDateFormatted}. 
                            ${declarationArrivalDate ? ("Metsänkäyttöilmoituksen saapumispäivä: " + declarationArrivalDate + ".") : ""}
                            ${declarationId ? ("MKI-tunnus: " + declarationId + ".") : ""}
                            ${standNumber ? ("Kuviotunnus: " + standNumber + ".") : ""}
                            ${cuttingRealizationPractices ? ("Hakkuutyyppi: " + cuttingRealizationPracticesText + ".") : ""}
                            ${(accuracyMeters && accuracyMeters !== 1) ? "HUOM! Hav. tarkkuus " + accuracyMeters + "m." : ""} 
                            <a style="color: lightblue" href="${graphic.attributes["unit.unitId"]}">Laji.fi-linkki</a>
                        </p>`;
                }
            }

            return "";
        }, [observationsContext.observationsState.allTaxons, observationsContext.observationsState.allCollections, i18n.language],
    );

    return {
        getObservationLine
    }
}
